import * as React from "react";
import {useEffect, useMemo} from "react";
import {Trans, useTranslation} from "react-i18next";
import window from "../../../lib/WindowExtended";
import {IIsVisible} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import "./Finished.scss";
import ShopExperience from "./ShopExperience";
import {updateUrlParams} from "../../../cart/CartIcon/CartIconPortal";
import {getReturnPolicyDays} from "../../../../helpers/getReturnPolicyDays";
import {Button} from "../../../lib/form/Button/Button";
import {getShopId} from "../../../lib/getShopId";

interface IFinishedBoughtItems {
  hasVoucher: boolean;
  hasItems: boolean;
}

const Finished: React.FC<IIsVisible> = (props) => {
  const {t} = useTranslation(["errorcodes", "translations"]);
  const {checkout} = useCheckoutContext();
  const orderNumber = checkout.orderId;
  const purchase = useMemo<IFinishedBoughtItems>(() => {
    return {
      hasItems:
        checkout.cartData.positions.find((position) => {
          return position.type === "Item" && position.item?.itemClassId !== 6;
        }) !== undefined,
      hasVoucher:
        checkout.cartData.positions.find((position) => {
          return position.type === "Item" && position.item?.itemClassId === 6;
        }) !== undefined,
    };
  }, []);

  useEffect(() => {
    if (props.isVisible) {
      updateUrlParams(null);
    }
  }, [props.isVisible]);

  if (!props.isVisible) {
    return null;
  }

  const ThankYou: React.FC = () => {
    const {invoiceAddress} = checkout;

    if (!invoiceAddress) {
      return null;
    }

    const customerName = `${invoiceAddress.firstName} ${invoiceAddress.lastName}`;

    return <h2>{t("general.thankYou--customerName", {customerName})}</h2>;
  };

  const WhatsNextBox: React.FC = () => {
    if (!purchase.hasItems) {
      return null;
    }

    return (
      <div className="shippingsteps">
        <p className="shippingsteps__box">
          <span className="shippingsteps__box__icon icon icon-mail">&nbsp;</span>
          <span className="shippingsteps__box__text">{t("checkout.orderConfirmationEmail")}</span>
        </p>
        <span className="shippingsteps__arrdown icon icon-down">&nbsp;</span>
        <p className="shippingsteps__box">
          <span className="shippingsteps__box__icon icon icon-truck">&nbsp;</span>
          <span className="shippingsteps__box__text">{t("general.alwaysInformed")}</span>
        </p>
        <span className="shippingsteps__arrdown icon icon-down">&nbsp;</span>
        <p className="shippingsteps__box">
          <span className="shippingsteps__box__icon icon icon-back">
            <span className="icon__content">{getReturnPolicyDays()}</span>
          </span>
          <span className="shippingsteps__box__text">
            {t("checkout.returnPolicyNote--days", {days: getReturnPolicyDays()})}
          </span>
        </p>
      </div>
    );
  };

  const AdditionalVoucherInformation: React.FC = () => {
    const infos: string[] = [];

    if (!purchase.hasVoucher) {
      return null;
    }

    infos.push(t("checkout.receiveGiftVoucherByEmail"));

    if (purchase.hasItems) {
      infos.push(" ", t("checkout.delayedVoucherShipping"));
    }

    return (
      <React.Fragment>
        <p className="shippingsteps__box">
          <span className="shippingsteps__box__icon icon icon-mail">&nbsp;</span>
          <span className="shippingsteps__box__text">
            {infos.map((text) => {
              return text;
            })}
          </span>
        </p>
        <br className="clear" />
      </React.Fragment>
    );
  };

  const timeNow = Date.now();
  /* Between 12. and 18.10.2025 */
  const isInTimeRange = 1760220000000 < timeNow && timeNow < 1760824800000;

  return (
    <div className="text">
      <ThankYou />
      <p>
        <strong>{t("checkout.successfulOrder")}</strong>
      </p>

      <p className="finished-order">
        <span className="icon icon-check--green icon-size--20">&#160;</span>
        {checkout.isGuest ? (
          t("checkout.orderNumber--orderNumber", {orderNumber})
        ) : (
          <span>
            <Trans
              i18nKey="checkout.finishOrderWithNumber--orderNumber"
              ns="translations"
              values={{orderNumber}}
              components={{
                b: <b />,
                a: <a href={t("url.userAccountLink")} target="_blank" rel="noreferrer" />,
              }}
            />
          </span>
        )}
      </p>

      <br className="clear" />

      <p>
        <strong>{t("general.whatHappensNext")}</strong>
      </p>

      <WhatsNextBox />
      <AdditionalVoucherInformation />
      <br />

      {getShopId() === 100001 && isInTimeRange && (
        <div className="download-box">
          <div className="whitepaper-image">
            <img
              src="https://cdn.connox.de/media/Whitepaper/LaH_Cover_11-25.png"
              width={180}
              height={235}
              alt="Herbstliche Inspiration für Sie"
            />
          </div>
          <div className="download-header">Herbstliche Inspiration für Sie</div>
          <div className="download-text">
            Als Dankeschön erhalten Sie exklusiv die aktuelle <span className="paper-title">„Living at Home“</span>{" "}
            als E-Paper – voller Ideen, Trends und Wohninspiration für die gemütliche Jahreszeit.
          </div>
          <Button as="link" href="https://cdn.connox.de/media/Whitepaper/ePaper_Connox_V2_Leseexemplar_FINAL.pdf">
            Jetzt herunterladen
          </Button>
        </div>
      )}

      {window.exponea && <ShopExperience />}

      {/* Sovendus-Container (zugehöriges JS wird im GTM eingebunden) */}
      <div>
        <div id="sovendus-container-1">{/* Empty */}</div>
        {/* TODO GTM greift nicht, weil es dynamisch reingeladen wird*/}
      </div>
    </div>
  );
};

export default Finished;
